import gql from 'graphql-tag';
import { differenceBy, findIndex, isArray, isFunction, pick } from 'lodash';

import {
	useCreateStakeMutation,
	useUpdateStakeMutation,
	useUpdateStakeScenarioMutation,
	useDeleteStakeMutation,
	useCreateTaggingMutation,
	useDeleteTaggingMutation,
} from 'graph/dist/generated';
import {
	getFieldFunctions,
	getFragmentFields,
	optimisticlyUpdateScenario,
	hideElementInOtherScenarios
} from 'utils/graphql';

export const useStakeController = () => {
	const [createStake] = useCreateStakeMutation();
	const [updateStake] = useUpdateStakeMutation();
	const [updateStakeScenario] = useUpdateStakeScenarioMutation();
	const [deleteStake] = useDeleteStakeMutation();
	const [createTagging] = useCreateTaggingMutation();
	const [deleteTagging] = useDeleteTaggingMutation();

	const updateTaggings = async (stake: any, tagIds: string[]) => {
		if (!tagIds || !isArray(tagIds)) return;
		const oldTaggings = stake.taggings || [];
		const newTaggings = tagIds.map((tagId) => ({ tagId, objectId: stake.id }));
		const toDelete = differenceBy(oldTaggings, newTaggings, 'tagId');
		const toAdd = differenceBy(newTaggings, oldTaggings, 'tagId');
		for (const tagging of toDelete) {
			await deleteTagging({
				variables: { id: tagging.id },
				update(cache, { data: { deleteTagging } }: any) {
					cache.modify({
						id: `Stake:${tagging.objectId}`,
						fields: {
							taggings: (taggings = []) =>
								taggings.filter((t: any) => t.__ref !== `Tagging:${deleteTagging.id}`)
						}
					});
				}
			});
		}
		for (const tagging of toAdd) {
			const variables = { projectId: stake.projectId, ...tagging, type: 'stake' };
			await createTagging({
				variables,
				update(cache, { data: { createTagging } }: any) {
					cache.modify({
						id: `Stake:${tagging.objectId}`,
						fields: {
							taggings: (taggings = []) => {
								const newRef = cache.writeFragment({
									id: 'Tagging:' + createTagging.id,
									data: createTagging,
									fragment: gql`fragment TF on Tagging { ${getFragmentFields(variables)} }`
								});
								return [...taggings, newRef];
							}
						}
					});
				}
			});
		}
	};

	const handleCreateStake = async (
		projectId: string,
		values: Object,
		defaults = {},
		scenario = null,
		scenarios: any[] = []
	) => {
		let baseDefaults = {
			weight: 1,
			trend: '50',
			description: '',
			reference: '',
			evaluatedAt: ''
		};

		let appliedDefaults = {
			...baseDefaults,
			...defaults
		};

		let variables = {
			projectId: projectId,
			name: values.name,
			weight: values.weight || appliedDefaults.weight,
			trend:
				values.trend !== null && values.trend !== undefined
					? values.trend
					: appliedDefaults.trend,
			description: values.description || appliedDefaults.description,
			reference: values.reference || appliedDefaults.reference,
			evaluatedAt: values.evaluatedAt || appliedDefaults.evaluatedAt,
			// Si on est dans un scénario, créer directement désactivé dans la baseline
			enabled: scenario ? false : true
		};

		console.log('values', values);
		console.log('variables', variables);

		let result = await createStake({
			variables,
			update(cache, { data: { createStake } }) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						stakes(stakes = []) {
							const newStake = cache.writeFragment({
								id: 'Stake:' + createStake.id,
								data: createStake,
								fragment: gql`
									fragment StakeFragment on Stake {
										${getFragmentFields(variables)}
									}
								`
							});

							return [...stakes, newStake];
						}
					}
				});
			},
			optimisticResponse: {
				createStake: {
					id: 'temp-id',
					...variables,
					createdAt: new Date().toISOString(),
					scenarios: []
				}
			}
		});

		// Si un scénario est actif, associer les valeurs au scénario (avec enabled: true)
		if (scenario && result.data?.createStake?.id) {
			const stake = result.data.createStake;

			const scenarioValues = {
				trend: variables.trend || '50',
				weight: variables.weight || 1,
				enabled: true
			};

			await optimisticlyUpdateScenario(
				stake,
				'Stake',
				scenario.id,
				scenarioValues,
				updateStakeScenario
			);
		}

		// Masquer l'élément dans tous les autres scénarios
		if (result.data?.createStake?.id && scenarios.length > 0) {
			await hideElementInOtherScenarios(
				result.data.createStake,
				'Stake',
				scenarios,
				scenario,
				updateStakeScenario
			);
		}

		return result;
	};

	const handleUpdateStake = async (id, stakes, scenario, values) => {
		console.log('handleUpdateStake', id, stakes, scenario, values);

		let index = findIndex(stakes, (s: any) => s.id === id);

		// Champs qui sont toujours stockés dans la baseline (non modifiables par scénario)
		const baselineFields = [
			'name',
			'reference',
			'description',
			'evaluatedAt'
		];

		// Champs qui peuvent être modifiés par scénario
		const scenarioFields = ['trend', 'weight', 'enabled'];

		// Vérifie si on a des champs baseline à mettre à jour
		const baselineValues = pick(values, baselineFields);
		const hasBaselineChanges = Object.keys(baselineValues).length > 0;

		// Vérifie si on a des champs scénario à mettre à jour
		const scenarioValuesFromForm = pick(values, scenarioFields);
		const hasScenarioChanges =
			Object.keys(scenarioValuesFromForm).length > 0;

		// Mise à jour des champs baseline (toujours, même si un scénario est actif)
		if (hasBaselineChanges) {
			let newStake = {
				...pick(stakes[index], Object.keys(baselineValues)),
				...baselineValues
			};

			if (values.evaluatedAt !== undefined)
				newStake.evaluatedAt =
					newStake.evaluatedAt === null ||
					!isFunction(newStake.evaluatedAt?.toISOString)
						? ''
						: newStake.evaluatedAt.toISOString();

			await updateStake({
				variables: {
					id: id,
					...newStake
				},
				update(cache) {
					cache.modify({
						id: `Stake:${id}`,
						fields: getFieldFunctions(baselineValues)
					});
				},
				optimisticResponse: {
					updateStake: true
				}
			});
		}

		// Mise à jour des champs scénario
		if (hasScenarioChanges) {
		if (scenario) {
			// Si un scénario est actif, envoyer uniquement les champs modifiés
			await optimisticlyUpdateScenario(
				stakes[index],
				'Stake',
				scenario.id,
				scenarioValuesFromForm,
				updateStakeScenario
			);
			} else {
				// Si pas de scénario, mettre à jour les valeurs baseline
				await updateStake({
					variables: {
						id: id,
						...scenarioValuesFromForm
					},
					update(cache) {
						cache.modify({
							id: `Stake:${id}`,
							fields: getFieldFunctions(scenarioValuesFromForm)
						});
					},
					optimisticResponse: {
						updateStake: true
					}
				});
			}
		}
	};

	const handleDelete = async (projectId: string, id: string | undefined) => {
		if (id == null) {
			return;
		}

		await deleteStake({
			variables: { id: id },
			update(cache) {
				cache.modify({
					id: `Project:${projectId}`,
					fields: {
						stakes(existingStakesRef, { readField }) {
							return existingStakesRef.filter(
								(ref: string) => id !== readField('id', ref)
							);
						}
					}
				});
			},

			optimisticResponse: {
				deleteStake: true
			}
		});
	};

	return {
		createStake: handleCreateStake,
		updateStake: handleUpdateStake,
		updateStakeTaggings: updateTaggings,
		deleteStake: handleDelete
	};
};
